import { useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { DIRECTOR_DRAWER_PARAM } from './ManagementProfileTabConstants';

/** Publishes `?directorDrawer=true` while the Director Details drawer is open — AskAicaV2 hides its
 * floating icon off that flag (isAnyDrawerOpen) — and clears it again, including on unmount. */
export const useDirectorDrawerUrlFlag = (isDrawerOpen: boolean) => {
    const [searchParams, setSearchParams] = useSearchParams();

    const isFlagPublished = useRef(false);

    // Do NOT depend the cleanup below on `setSearchParams`: react-router gives it a new identity on
    // every search-param write, so the cleanup would run while still mounted and undo the flag.
    const setSearchParamsRef = useRef(setSearchParams);
    useEffect(() => {
        setSearchParamsRef.current = setSearchParams;
    }, [setSearchParams]);

    useEffect(() => {
        const nextParams = new URLSearchParams(searchParams);
        const isFlagged = nextParams.get(DIRECTOR_DRAWER_PARAM) === 'true';
        isFlagPublished.current = isFlagged;
        if (isDrawerOpen && !isFlagged) {
            nextParams.set(DIRECTOR_DRAWER_PARAM, 'true');
            isFlagPublished.current = true;
            setSearchParams(nextParams, { replace: true });
        } else if (!isDrawerOpen && isFlagged) {
            nextParams.delete(DIRECTOR_DRAWER_PARAM);
            isFlagPublished.current = false;
            setSearchParams(nextParams, { replace: true });
        }
    }, [isDrawerOpen, searchParams, setSearchParams]);

    // A tab switch unmounts this tab, so the effect above can't clear the flag on the way out and
    // navigateToTab would carry it to the next tab.
    useEffect(
        () => () => {
            if (!isFlagPublished.current) return;
            isFlagPublished.current = false;
            setSearchParamsRef.current(
                (previous) => {
                    const nextParams = new URLSearchParams(previous);
                    nextParams.delete(DIRECTOR_DRAWER_PARAM);
                    return nextParams;
                },
                { replace: true },
            );
        },
        [],
    );
};

export default useDirectorDrawerUrlFlag;
